/**
 * Every published Adkiro price. The homepage, the services and pricing page,
 * the assistant and the structured data all read from here, so a price
 * changes in one place. Keep src/lib/chat-knowledge.ts in step.
 *
 * Premium on purpose: the buyers are agencies and developers selling
 * high-value property, mostly in Dubai. On 1 Oct 2026 Kristian rejected a
 * cheaper list ($290 creatives, $750 campaigns, $1,500 a month) as the wrong
 * signal for them.
 */
export interface Offer {
  id: "review" | "launch" | "agency" | "developer" | "portfolio";
  name: string;
  price: string;
  /** For structured data. Absent when the price is set in the proposal. */
  schemaPrice?: number;
  cadence: string;
  fit: string;
  blurb: string;
  features: string[];
  /** Stripe Payment Link. Absent until one exists; the service's row then offers only the free review. */
  payUrl?: string;
  /** The pay button's words, when "Pay" and the price do not fit. */
  payLabel?: string;
}

export const AUDIT_OFFER: Offer = {
  id: "review",
  name: "Senior Meta Ads Account Review",
  price: "$950",
  schemaPrice: 950,
  cadence: "one-time",
  fit: "For accounts already advertising on Meta",
  blurb:
    "A written diagnostic of your existing campaigns: what works, what wastes budget and what to fix first.",
  features: [
    "Campaign, creative and measurement diagnostic",
    "Written senior review",
    "Prioritised 90-day roadmap",
    "Implementation handover, sent by email",
    "Delivered within three business days of access",
    "$950 credited to the first month of management",
  ],
  payUrl: "https://buy.stripe.com/5kQ8wJ4jz9Ag4k0dU9g7e01",
};

export const LAUNCH_OFFER: Offer = {
  id: "launch",
  name: "Property Launch",
  price: "$2,500",
  schemaPrice: 2500,
  cadence: "one-time",
  fit: "One listing or one project",
  blurb:
    "A complete Meta campaign for one property or project: premium creative, setup and 30 days of senior management.",
  features: [
    "Campaign plan and setup in your own ad account",
    "Premium creatives from your photos, renders and floor plans",
    "Enquiries by instant form or WhatsApp",
    "30 days of management and optimisation",
    "A written update every week",
  ],
  payUrl: "https://buy.stripe.com/aFaeV72br8wc4k017ng7e02",
};

export const AGENCY_OFFER: Offer = {
  id: "agency",
  name: "Agency Growth",
  price: "From $3,500",
  schemaPrice: 3500,
  cadence: "/month",
  fit: "Agencies and brokers",
  blurb: "Always-on Meta advertising for a steady flow of listings, buyers and sellers.",
  features: [
    "Buyer and seller enquiry campaigns",
    "Premium creatives for new listings as they launch",
    "Lead quality reviewed with your team",
    "A written report every month",
    "Direct line to Kristian by email and WhatsApp",
  ],
  payUrl: "https://buy.stripe.com/9B6cMZeYd9Ag4k0g2hg7e03",
  payLabel: "Pay $3,500 / month",
};

export const DEVELOPER_OFFER: Offer = {
  id: "developer",
  name: "Developer Launch",
  price: "From $7,500",
  schemaPrice: 7500,
  cadence: "/month",
  fit: "Developers and new projects",
  blurb:
    "Launch and sales-phase campaigns for a development, from first release to the last units.",
  features: [
    "Launch and registration-of-interest campaigns",
    "Viewing and sales-centre enquiries",
    "Creative testing across units and buyer types",
    "Targeting across the UAE, Europe and the US",
    "A written report every week",
  ],
  payUrl: "https://buy.stripe.com/aFabIV6rH6o48Ag03jg7e04",
  payLabel: "Pay $7,500 / month",
};

export const PORTFOLIO_OFFER: Offer = {
  id: "portfolio",
  name: "Portfolio",
  price: "By proposal",
  cadence: "",
  fit: "Several projects or markets",
  blurb:
    "For developers and groups running several projects, markets or ad accounts at the same time.",
  features: [
    "Several projects, markets or ad accounts",
    "One measurement framework across all of them",
    "Launch calendar and budget allocation",
    "A weekly written report and a monthly review",
  ],
  payUrl: "https://buy.stripe.com/dRm9AN5nD9Ag9Ek2brg7e05",
  payLabel: "Pay the agreed amount",
};

/** The words on an offer's pay button, the same wherever the button appears. */
export const payLabel = (offer: Offer) => offer.payLabel ?? `Pay ${offer.price}`;

export const ONE_OFF_OFFERS = [AUDIT_OFFER, LAUNCH_OFFER];
export const MONTHLY_OFFERS = [AGENCY_OFFER, DEVELOPER_OFFER, PORTFOLIO_OFFER];
export const OFFERS = [...ONE_OFF_OFFERS, ...MONTHLY_OFFERS];

export const MONTHLY_TERMS =
  "Monthly plans renew by card each month, with a three-month minimum term, then continue month to month.";
export const AD_SPEND_NOTE =
  "Advertising spend is paid by you directly to Meta and is never part of the fee.";

/** Said under the main buttons, so the first step reads as free before anyone clicks. */
export const FIT_REVIEW_NOTE =
  "Free and without obligation: a written reply within one business day, with the service that fits and its price.";
